import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90 border-transparent',
        secondary: 'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 border-transparent',
        destructive:
          'bg-destructive [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 border-transparent text-white',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        // Status variants for dashboard detail-table badges — soft tinted pills
        // that keep ≥4.5:1 contrast in both themes (semantic theme tokens only).
        success:
          'border-transparent bg-semantic-success-50 text-semantic-success-700 dark:bg-semantic-success-500/15 dark:text-semantic-success-400',
        warning:
          'border-transparent bg-semantic-yellow-50 text-semantic-yellow-700 dark:bg-semantic-yellow-500/15 dark:text-semantic-yellow-400',
        neutral:
          'border-transparent bg-grey-100 text-text-medium-emphasis dark:bg-grey-100/10',
        // Pre-release marker ("Beta") beside a heading, nav row or eyebrow — the
        // grain micro-label voice on the accent-soft tint (≥4.5:1 in every palette).
        // A caps pill like the status badge; own leading so the chip is the same
        // height in an h1, a nav row and an eyebrow instead of inheriting theirs.
        beta: 'border-transparent bg-[var(--dash-accent-soft)] px-1.5 py-0 text-[9.5px] leading-[1.5] font-bold tracking-[.12em] text-[color:var(--dash-accent-ink)] uppercase'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return <Comp data-slot='badge' className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }